// PATH: src/components/documents/ChapterObjectivesTab.tsx
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import RefreshIcon from "@mui/icons-material/Refresh";

import type { DocumentObjective } from "../../api/objectives/objectives";
import { EmptyState, StatusPill, useConfirm, useToast } from "../ui";
import { compareByKind, computeCoverage, formatNotionList } from "./objectivesView";
import { formatLessonsAttention, groupByLesson, lessonStatusInfo, summaryTone } from "./lessonCheck";
import LessonsRunAlert from "./LessonsRunAlert";
import { useAuth } from "../../context/AuthContext";
import type { ChapterReview } from "./useChapterReview";

interface Props {
  review: ChapterReview;
  /** Ouvre la base de faits d'un objectif (vue de détail du panneau). */
  onOpenObjective: (objective: DocumentObjective) => void;
  /** Une extraction a abouti. */
  onExtracted?: () => void;
  /** Ouvre l'onglet « Leçons ». */
  onShowLessons?: () => void;
}

/**
 * Onglet « Objectifs et faits » : les objectifs extraits du chapitre, rangés
 * sous leur leçon (chapitre importé), leur provenance dans le cours, leur
 * nature, et s'ils ont une base de faits.
 */
export const ChapterObjectivesTab: React.FC<Props> = ({
  review,
  onOpenObjective,
  onExtracted,
  onShowLessons,
}) => {
  const confirm = useConfirm();
  const toast = useToast();
  // Relecteur : lecture seule, aucune extraction (appels payants, écriture).
  const { canEdit } = useAuth();
  const { objectives, objectivesState, fromCache, extracting, replacement, lessonsRun, extractError, lessons } = review;

  /* Filtres : on cherche d'abord les objectifs SANS base (ceux qui ne
     serviront qu'un QCM) et ceux d'une nature donnée. */
  const [onlyWithoutKnowledge, setOnlyWithoutKnowledge] = React.useState(false);
  const [kindFilter, setKindFilter] = React.useState("all");
  const [sortByKind, setSortByKind] = React.useState(false);

  const coverage = React.useMemo(() => computeCoverage(objectives), [objectives]);
  const kinds = React.useMemo(() => {
    const found = new Set<string>();
    objectives.forEach((o) => o.kind && found.add(o.kind));
    return [...found].sort((a, b) => a.localeCompare(b));
  }, [objectives]);
  const withoutKnowledgeCount = React.useMemo(() => objectives.filter((o) => !o.has_knowledge).length, [objectives]);

  const visible = React.useMemo(() => {
    const filtered = objectives.filter((o) => {
      if (onlyWithoutKnowledge && o.has_knowledge) return false;
      if (kindFilter !== "all" && (o.kind ?? "") !== kindFilter) return false;
      return true;
    });
    return sortByKind ? [...filtered].sort(compareByKind) : filtered;
  }, [objectives, onlyWithoutKnowledge, kindFilter, sortByKind]);

  // Chapitre importé : les objectifs sous leur leçon ; sinon liste plate.
  const groups = React.useMemo(() => groupByLesson(visible, lessons), [visible, lessons]);
  const filtering = visible.length !== objectives.length;

  const runExtract = async (force: boolean) => {
    if (force) {
      const ok = await confirm({
        title: "Ré-extraire les objectifs ?",
        message:
          "Les objectifs du chapitre sont recalculés, leçon par leçon. Ceux qui gardent le même sens dans leur " +
          "leçon conservent leur identifiant (et la progression des élèves) ; les autres deviennent orphelins : " +
          "leur progression est perdue, avec leurs corrections éditoriales et signalements.\n\n" +
          "Le bilan par leçon s'affiche à la fin.",
        confirmLabel: "Ré-extraire",
      });
      if (!ok) return;
    }
    const res = await review.extract(force);
    if (res) {
      toast.success(`${res.objectives.length} objectif(s) extrait(s).`);
      onExtracted?.();
    }
  };

  if (objectivesState === "loading") {
    return (
      <Stack spacing={1}>
        <Skeleton variant="rounded" height={40} />
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={56} />
        ))}
      </Stack>
    );
  }

  if (objectivesState === "error") {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => review.reloadObjectives()}>
            Réessayer
          </Button>
        }
      >
        Impossible de charger les objectifs.
      </Alert>
    );
  }

  const banners = (
    <>
      {extractError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractError}
        </Alert>
      )}
      {replacement && (
        <Alert severity={replacement.severity} sx={{ mb: 2 }}>
          {replacement.text}
          {replacement.severity === "warning"
            ? " Un objectif orphelin repart de zéro : sa base de faits sera ré-extraite, mais les faits " +
              "désactivés à la main et les signalements des élèves ne reviendront pas."
            : null}
        </Alert>
      )}
      {lessonsRun && <LessonsRunAlert view={lessonsRun} sx={{ mb: 2 }} />}
    </>
  );

  if (objectives.length === 0) {
    return (
      <>
        {banners}
        <Paper variant="outlined">
          <EmptyState
            icon={<FormatListBulletedIcon />}
            title="Aucun objectif extrait"
            description={
              canEdit
                ? "L'extraction lit le chapitre leçon par leçon et rattache chaque objectif à sa leçon. Elle prend une à deux minutes."
                : "Les objectifs de ce chapitre n'ont pas encore été extraits."
            }
            action={
              canEdit ? (
                <Button
                  variant="contained"
                  onClick={() => void runExtract(false)}
                  disabled={extracting}
                  startIcon={extracting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {extracting ? "Extraction en cours…" : "Extraire les objectifs"}
                </Button>
              ) : undefined
            }
          />
        </Paper>
      </>
    );
  }

  const renderObjective = (obj: DocumentObjective) => (
    <ButtonBase
      key={obj.id}
      onClick={() => onOpenObjective(obj)}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        textAlign: "start",
        gap: 1.5,
        px: 2,
        py: 1.25,
        width: "100%",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        className="mono"
        sx={{ minWidth: 24, textAlign: "right", pt: 0.25 }}
      >
        {obj.ordinal}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" dir="auto">
          {obj.text}
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
          {/* Provenance : le passage du cours qui fonde cet objectif. */}
          {obj.source_chunk_ordinals?.length ? (
            <StatusPill tone="neutral" dot={false} title="Passages du cours qui fondent cet objectif">
              {obj.source_chunk_ordinals.length > 1 ? "notions" : "notion"}{" "}
              {formatNotionList(obj.source_chunk_ordinals)}
            </StatusPill>
          ) : (
            <StatusPill tone="warning">sans provenance</StatusPill>
          )}
          {/* Nature : ce que le gabarit d'exercice pourra viser. */}
          {obj.kind ? (
            <StatusPill
              tone={obj.kind_confidence != null && obj.kind_confidence < 0.5 ? "warning" : "info"}
              dot={false}
            >
              {obj.kind_confidence != null
                ? `${obj.kind} · ${Math.round(obj.kind_confidence * 100)} %`
                : obj.kind}
            </StatusPill>
          ) : null}
          {obj.language ? (
            <StatusPill tone="neutral" dot={false}>
              {obj.language}
            </StatusPill>
          ) : null}
          {/* Sans unité de faits, les formes construites sont
              inéligibles : l'élève n'aura qu'un QCM. */}
          {obj.has_knowledge ? (
            <StatusPill tone="success">base de faits</StatusPill>
          ) : (
            <StatusPill tone="warning">pas de base de faits</StatusPill>
          )}
        </Stack>
      </Box>
      <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary", mt: 0.25 }} />
    </ButtonBase>
  );

  const gapCount = coverage.gaps.length;
  // Leçons à reprendre, lues sur l'onglet Leçons (même source, plus fraîche que la liste).
  const lessonAttention = lessons ? formatLessonsAttention(lessons.summary) : "";

  return (
    <Box>
      {lessonAttention && lessons && !lessonsRun && (
        <Alert
          severity={summaryTone(lessons.summary) === "error" ? "error" : "warning"}
          sx={{ mb: 2, alignItems: "flex-start" }}
          action={
            <Stack direction={{ xs: "column", sm: "row" }} spacing={0.5}>
              {onShowLessons && (
                <Button color="inherit" size="small" onClick={onShowLessons} sx={{ whiteSpace: "nowrap" }}>
                  Voir les leçons
                </Button>
              )}
              {canEdit && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void runExtract(true)}
                  disabled={extracting}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Ré-extraire
                </Button>
              )}
            </Stack>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            {lessonAttention}
          </Typography>
          <Typography variant="body2">
            Une leçon vide n'a aucun objectif : l'élève ne la travaillera pas. Une leçon faible a moins de 2 objectifs,
            ou des objectifs sans base de faits.
          </Typography>
        </Alert>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <StatusPill tone="primary" dot={false}>
            {objectives.length} objectif{objectives.length > 1 ? "s" : ""}
          </StatusPill>
          {coverage.covered > 0 && (
            <StatusPill tone="success" dot={false}>
              {coverage.covered} passage{coverage.covered > 1 ? "s" : ""} couvert{coverage.covered > 1 ? "s" : ""}
            </StatusPill>
          )}
          {withoutKnowledgeCount > 0 && (
            <StatusPill tone="warning">{withoutKnowledgeCount} sans base de faits</StatusPill>
          )}
          {fromCache && (
            <StatusPill tone="neutral" dot={false} title="Objectifs relus depuis la base, pas recalculés">
              déjà extraits
            </StatusPill>
          )}
        </Stack>
        {canEdit && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => void runExtract(true)}
            disabled={extracting}
            startIcon={extracting ? <CircularProgress size={14} /> : <RefreshIcon />}
            sx={{ flexShrink: 0 }}
          >
            {extracting ? "Ré-extraction…" : "Ré-extraire"}
          </Button>
        )}
      </Stack>

      {banners}

      {gapCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {gapCount > 1
            ? `${gapCount} notions (passages) ne sont la source d'aucun objectif`
            : "Une notion (passage) n'est la source d'aucun objectif"}{" "}
          — les résumés de leçon en font souvent partie, ce n'est pas forcément un manque.{" "}
          {gapCount > 1 ? "Notions" : "Notion"} {formatNotionList(coverage.gaps)}.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ px: 2, py: 1.25, bgcolor: "background.default", borderBottom: 1, borderColor: "divider" }}
        >
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                checked={onlyWithoutKnowledge}
                onChange={(e) => setOnlyWithoutKnowledge(e.target.checked)}
                sx={{ mr: 1 }}
              />
            }
            label={<Typography variant="body2">Sans base de faits ({withoutKnowledgeCount})</Typography>}
          />
          {kinds.length > 0 && (
            <TextField
              select
              label="Nature"
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">Toutes</MenuItem>
              {kinds.map((kind) => (
                <MenuItem key={kind} value={kind}>
                  {kind}
                </MenuItem>
              ))}
            </TextField>
          )}
          <FormControlLabel
            sx={{ m: 0 }}
            control={<Switch checked={sortByKind} onChange={(e) => setSortByKind(e.target.checked)} sx={{ mr: 1 }} />}
            label={<Typography variant="body2">Trier par nature</Typography>}
          />
          {visible.length !== objectives.length && (
            <Typography variant="body2" color="text.secondary">
              {visible.length} sur {objectives.length}
            </Typography>
          )}
        </Stack>

        {visible.length === 0 ? (
          <EmptyState dense title="Aucun objectif ne correspond aux filtres" />
        ) : groups ? (
          groups
            // Filtre actif : seulement les leçons qui ont un objectif visible.
            .filter((g) => !filtering || g.objectives.length > 0)
            .map((g) => {
              const info = g.status ? lessonStatusInfo(g.status) : null;
              return (
                <Box key={g.key || "sans-lecon"}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ px: 2, py: 0.75, bgcolor: "background.default", borderBottom: 1, borderTop: 1, borderColor: "divider", mt: "-1px" }}
                  >
                    {g.ordre != null && (
                      <Typography variant="caption" className="mono" color="text.secondary">
                        {g.ordre}
                      </Typography>
                    )}
                    <Typography variant="subtitle2" dir="auto" sx={{ minWidth: 0, flex: 1 }}>
                      {g.key ? g.titre || g.key : "Sans leçon"}
                    </Typography>
                    {info && <StatusPill tone={info.tone}>{info.label}</StatusPill>}
                  </Stack>
                  {g.objectives.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.25 }}>
                      Aucun objectif déduit de cette leçon.
                    </Typography>
                  ) : (
                    <Stack divider={<Divider flexItem />}>{g.objectives.map(renderObjective)}</Stack>
                  )}
                </Box>
              );
            })
        ) : (
          <Stack divider={<Divider flexItem />}>{visible.map(renderObjective)}</Stack>
        )}
      </Paper>
    </Box>
  );
};

export default ChapterObjectivesTab;
